<template>
  <el-dialog
    :model-value="modelValue"
    :title="detail?.title || '결재'"
    :width="dialogWidth"
    class="approval-detail-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div v-loading="loading">
      <template v-if="detail">
        <!-- 기본 정보 -->
        <div class="meta-row">
          <el-tag size="small">{{ documentTypeLabel }}</el-tag>
          <span class="meta-item">문서번호 {{ detail.document_number }}</span>
          <span class="meta-item">기안자 {{ detail.requester_name }}</span>
          <span class="meta-item">상신일 {{ formatDate(detail.submitted_at) }}</span>
          <el-tag :type="statusType(detail.status)" size="small">{{ detail.status_display || detail.status }}</el-tag>
        </div>

        <!-- 요약 (상신 시점 스냅샷) -->
        <el-card v-if="detail.document_summary?.length" shadow="never" class="summary-card">
          <template v-for="(item, i) in detail.document_summary" :key="i">
            <div v-if="item.type === 'image' && item.value" class="summary-image">
              <el-image
                :src="item.value"
                fit="contain"
                style="max-height: 160px; width: 100%;"
                :preview-src-list="[item.value]"
                preview-teleported
              >
                <template #error>
                  <div class="image-error">이미지를 불러올 수 없습니다</div>
                </template>
              </el-image>
            </div>
            <div v-else class="summary-kv">
              <span class="k">{{ item.label }}</span>
              <span class="v">{{ item.value }}</span>
            </div>
          </template>
        </el-card>
        <div v-if="detail.memo" class="memo-block">기안 메모: {{ detail.memo }}</div>

        <!-- 결재선 -->
        <div class="steps-title">결재선</div>
        <div v-for="s in detail.approval_steps" :key="s.id" class="step-row">
          <span class="step-seq">{{ s.sequence }}</span>
          <span class="step-name">{{ s.approver_name }}</span>
          <el-tag :type="stepStatusType(s.status)" size="small">{{ s.status_display || s.status }}</el-tag>
          <span v-if="s.comments" class="step-comments">{{ s.comments }}</span>
        </div>

        <!-- 처리 코멘트 -->
        <el-input
          v-if="canProcess"
          v-model="processComments"
          type="textarea"
          :rows="2"
          placeholder="코멘트 (반려 시 필수)"
          class="comments-input"
        />
      </template>
    </div>
    <template #footer>
      <div class="dialog-actions" :class="{ 'with-detail': detailPath }">
        <el-button v-if="detailPath" text type="primary" class="detail-link" @click="goDetail">
          상세 화면 보기 ›
        </el-button>
        <el-button @click="$emit('update:modelValue', false)">닫기</el-button>
        <template v-if="canProcess">
          <el-button type="danger" plain :loading="processing" @click="doReject">반려</el-button>
          <el-button type="success" :loading="processing" @click="doApprove">승인</el-button>
        </template>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import { useApprovalStore } from '@/stores/approvalStore'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  approvalId: { type: [Number, String], default: null },
  // false면 조회 전용 (처리 버튼 숨김)
  allowProcess: { type: Boolean, default: true },
  // 문서 상세 페이지 경로 (있으면 푸터에 "상세 화면 보기" 노출)
  detailPath: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue', 'processed'])

const router = useRouter()
const goDetail = () => {
  emit('update:modelValue', false)
  router.push(props.detailPath)
}

const approvalStore = useApprovalStore()

const loading = ref(false)
const detail = ref(null)
const processComments = ref('')
const processing = ref(false)

const dialogWidth = computed(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? '95%' : '640px'))
// 처리 버튼: 호출측 허용 + 진행중 상태일 때만 (실제 권한·순서 검증은 서버가 수행)
const canProcess = computed(() => props.allowProcess && detail.value?.status === 'PENDING')

const documentTypeLabels = {
  sample_request: '샘플요청',
  expense: '지출결의서',
  quotation: '견적서',
  material_order: '자재발주',
  main_request: '메인오더',
  leave: '휴가',
  daily_cash_report: '자금일보',
  certificate_request: '증명서 신청',
}
const documentTypeLabel = computed(
  () => documentTypeLabels[detail.value?.document_type] || detail.value?.document_type || ''
)

const formatDate = (dt) => (dt ? dt.substring(0, 10) : '-')
const statusType = (status) => {
  const map = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger', CANCELLED: 'info', DRAFT: 'info' }
  return map[status] || 'info'
}
const stepStatusType = (status) => {
  const map = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger', INVALIDATED: 'info', SKIPPED: 'info', ACKNOWLEDGED: 'info' }
  return map[status] || 'info'
}

const fetchDetail = async () => {
  if (!props.approvalId) return
  loading.value = true
  detail.value = null
  processComments.value = ''
  try {
    const res = await axiosInstance.get(`approvals/${props.approvalId}/`)
    detail.value = res.data
  } catch (err) {
    console.error('결재 상세 조회 실패:', err)
    ElMessage.error('결재 정보를 불러오지 못했습니다.')
    emit('update:modelValue', false)
  } finally {
    loading.value = false
  }
}

watch(
  () => [props.modelValue, props.approvalId],
  ([visible]) => {
    if (visible) fetchDetail()
  }
)

const errMsg = (err, fallback) => {
  const msg = err?.response?.data?.error || err?.response?.data?.detail || fallback
  return typeof msg === 'string' ? msg : JSON.stringify(msg)
}

const doApprove = async () => {
  processing.value = true
  try {
    await approvalStore.approve(detail.value.id, processComments.value)
    ElMessage.success('승인했습니다.')
    emit('update:modelValue', false)
    emit('processed')
  } catch (err) {
    ElMessage.error(errMsg(err, '승인에 실패했습니다.'))
  } finally {
    processing.value = false
  }
}

const doReject = async () => {
  if (!processComments.value.trim()) {
    ElMessage.warning('반려 사유를 입력하세요.')
    return
  }
  processing.value = true
  try {
    await approvalStore.reject(detail.value.id, processComments.value)
    ElMessage.success('반려했습니다.')
    emit('update:modelValue', false)
    emit('processed')
  } catch (err) {
    ElMessage.error(errMsg(err, '반려에 실패했습니다.'))
  } finally {
    processing.value = false
  }
}
</script>

<style scoped>
.meta-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.meta-item {
  font-size: 13px;
  color: #606266;
}
.summary-card {
  margin-bottom: 12px;
  border-left: 4px solid #409eff;
}
.summary-kv {
  display: flex;
  gap: 12px;
  padding: 4px 0;
  font-size: 14px;
}
.summary-kv .k {
  width: 90px;
  flex-shrink: 0;
  color: #909399;
}
.summary-kv .v {
  color: #303133;
  word-break: break-word;
}
.summary-image {
  margin-bottom: 8px;
  text-align: center;
}
.image-error {
  font-size: 12px;
  color: #c0c4cc;
  padding: 20px 0;
}
.memo-block {
  font-size: 13px;
  color: #606266;
  margin-bottom: 12px;
}
.steps-title {
  font-size: 13px;
  font-weight: 700;
  margin: 8px 0 6px;
}
.step-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 13px;
}
.step-seq {
  width: 18px;
  color: #909399;
}
.step-comments {
  color: #909399;
  font-size: 12px;
}
.comments-input {
  margin-top: 12px;
}
.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.dialog-actions.with-detail .detail-link {
  margin-right: auto;
}
@media (max-width: 768px) {
  .dialog-actions {
    flex-direction: column-reverse;
  }
  .dialog-actions .el-button {
    width: 100%;
    margin-left: 0;
  }
}
</style>
